import { CSSProperties, useState } from 'react';
import * as R from 'ramda';

import { Button, Progress } from '@atom/mui';
import {
  RelatedField,
  RelatedInputData,
  Task,
  TaskField,
} from '@atom/types/task';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import RequiredFieldsConfirmModal from '../requiredFieldsConfirmModal/RequiredFieldsConfirmModal';

import TaskRelatedField from './TaskRelatedField';
import TaskRelatedFieldModal from './TaskRelatedFieldModal';

export const convertRelatedInputsToTaskValue = values => {
  return R.keys(values).map(title => ({
    title,
    value: values[title],
  }));
};

const NOOP = () => null;

const styles: Record<string, CSSProperties> = {
  fieldsContainer: {
    display: 'flex',
    flexFlow: 'row wrap',
  },
  editButton: {
    minWidth: '2.5rem',
  },
};

interface Props {
  field: TaskField;
  workOrderId?: string;
  taskId?: string;
  task?: Task;
  onChange: (fieldId: string, value: any) => void;
  isTemplate?: boolean;
  error?: boolean;
  onClick?: () => void;
  isClosed?: boolean;
  refetch?: () => void;
}

const TaskRelatedFieldGroup = ({
  field,
  workOrderId,
  taskId,
  task,
  onChange,
  isTemplate,
  error = false,
  isClosed = false,
  refetch,
}: Props) => {
  const [openFieldsConfirm, setOpenFieldsConfirm] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const { id: fieldId } = field;
  const fieldValues: RelatedField[] = field?.value;

  const handleUpdate = (newFieldValue: RelatedInputData) => {
    onChange(fieldId, convertRelatedInputsToTaskValue(newFieldValue));
    setOpen(false);
  };

  const canEdit =
    hasRolePermissions(ROLE_SETS.INSPECTOR) && !isTemplate && !isClosed;

  const onProceedComplete = () => {
    refetch();
    setOpenFieldsConfirm(false);
    setOpen(true);
  };

  const handleEdit = () => {
    if (task?.isCompleted) {
      setOpenFieldsConfirm(true);
      return;
    }
    setOpen(true);
  };

  return !fieldValues ? (
    <Progress />
  ) : (
    <>
      <div style={styles.fieldsContainer}>
        {fieldValues.map(relatedField => (
          <TaskRelatedField
            error={error && R.isNil(relatedField.value)}
            relatedField={relatedField}
            disabled={task?.isCompleted}
          />
        ))}
        {canEdit && (
          <div onClick={task?.isCompleted ? handleEdit : NOOP}>
            <Button
              onClick={handleEdit}
              color={task?.isCompleted ? 'inherit' : 'primary'}
              style={styles.editButton}
              disabled={task?.isCompleted}
            >
              EDIT
            </Button>
          </div>
        )}
      </div>
      <TaskRelatedFieldModal
        open={open}
        workOrderId={workOrderId}
        fieldId={fieldId}
        taskId={taskId}
        fieldValues={fieldValues}
        onClose={() => setOpen(false)}
        onChange={handleUpdate}
      />
      <RequiredFieldsConfirmModal
        open={openFieldsConfirm}
        onCancel={() => setOpenFieldsConfirm(false)}
        onComplete={onProceedComplete}
        task={task}
        workOrderId={workOrderId}
      />
    </>
  );
};

export default TaskRelatedFieldGroup;
